import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Bus, TrendingUp, Calendar, Users, Banknote, 
  MapPin, Clock, Star, Plus, ChevronRight, BarChart3,
  AlertCircle, CheckCircle, Settings, LogOut, UserPlus, Ticket, ScanLine, ClipboardList, Download
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid
} from 'recharts';

const BusOperatorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [operator, setOperator] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [feesReport, setFeesReport] = useState(null);
  const monthStart = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-01`;
  };
  const today = () => new Date().toISOString().slice(0, 10);
  const [feeRange, setFeeRange] = useState({ from: monthStart(), to: today() });
  const [feesLoading, setFeesLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      navigate('/bus/operator/login', { state: { returnTo: '/bus/operator' } });
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [profileRes, dashboardRes, schedulesRes] = await Promise.all([
        api.get('/bus/operators/profile'),
        api.get('/bus/operators/dashboard'),
        api.get('/bus/operators/schedules')
      ]);
      
      setOperator(profileRes.data.operator);
      setDashboard(dashboardRes.data.dashboard);
      
      // Get recent bookings from schedules
      // For now, just use schedules as a proxy
      setRecentBookings(schedulesRes.data.schedules?.slice(0, 5) || []);

      // This-month earnings & platform fees (operator-scoped fees report)
      fetchFees(feeRange);
    } catch (error) {
      if (error.response?.status === 404) {
        // Not registered as operator
        toast({
          title: 'Not Registered',
          description: 'Please register as a bus operator first',
        });
        navigate('/bus/operator/apply');
      } else {
        console.error('Error:', error);
        toast({
          title: 'Error',
          description: 'Failed to load dashboard',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFees = async (range) => {
    setFeesLoading(true);
    try {
      const feesRes = await api.get('/bus/admin/fees-report', {
        params: { from: range.from, to: range.to, group_by: 'day' }
      });
      setFeesReport(feesRes.data);
    } catch (e) {
      // operator may not have an approved profile / no data yet — non-blocking
      setFeesReport(null);
    } finally {
      setFeesLoading(false);
    }
  };

  const downloadFeesCsv = async () => {
    setCsvLoading(true);
    fetchFees(feeRange); // keep on-screen totals in sync with the exported range
    try {
      const res = await api.get('/bus/admin/fees-report.csv', {
        params: { from: feeRange.from, to: feeRange.to, group_by: 'day' },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fees-report_${feeRange.from}_${feeRange.to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Download failed', description: 'Could not export CSV. Try again.', variant: 'destructive' });
    } finally {
      setCsvLoading(false);
    }
  };

  const handleLogout = () => {
    navigate('/bus');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const isPending = operator?.status === 'pending';
  const isSuspended = operator?.status === 'suspended';

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-6xl mx-auto p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bus className="h-8 w-8" />
              <div>
                <h1 className="text-xl font-bold">{operator?.companyName || 'Bus Operator'}</h1>
                <p className="text-indigo-200 text-sm">Operator Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                className="text-white hover:bg-white/10"
                onClick={() => navigate('/bus/operator/settings')}
              >
                <Settings className="h-5 w-5" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                className="text-white hover:bg-white/10"
                onClick={handleLogout}
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4">
        {/* Pending Approval Notice */}
        {isPending && (
          <Card className="p-4 mb-4 bg-yellow-50 border-yellow-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-yellow-600" />
              <div>
                <h3 className="font-semibold text-yellow-800">Application Pending</h3>
                <p className="text-yellow-700 text-sm">
                  Your application is under review. You'll be able to add buses and routes once approved.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Suspended Notice */}
        {isSuspended && (
          <Card className="p-4 mb-4 bg-red-50 border-red-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-red-600" />
              <div>
                <h3 className="font-semibold text-red-800">Account Suspended</h3>
                <p className="text-red-700 text-sm">
                  Your account has been suspended. Please contact support for assistance.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Bus className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Buses</p>
                <p className="text-2xl font-bold">{dashboard?.totalBuses || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <MapPin className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Routes</p>
                <p className="text-2xl font-bold">{dashboard?.totalRoutes || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Bookings</p>
                <p className="text-2xl font-bold">{dashboard?.totalBookings || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Banknote className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold">K{dashboard?.totalRevenue?.toLocaleString() || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* This-Month Revenue & Platform Fees (from realized ledger) */}
        <Card className="p-5 mb-6 bg-gradient-to-br from-indigo-50 to-white border-indigo-200" data-testid="operator-earnings-widget">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">Revenue & Fees</h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="date"
                value={feeRange.from}
                max={feeRange.to}
                onChange={(e) => setFeeRange({ ...feeRange, from: e.target.value })}
                className="text-xs border border-gray-300 rounded-md px-2 py-1"
                data-testid="earnings-from-input"
              />
              <span className="text-xs text-gray-400">to</span>
              <input
                type="date"
                value={feeRange.to}
                min={feeRange.from}
                max={today()}
                onChange={(e) => setFeeRange({ ...feeRange, to: e.target.value })}
                className="text-xs border border-gray-300 rounded-md px-2 py-1"
                data-testid="earnings-to-input"
              />
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                disabled={feesLoading}
                onClick={() => fetchFees(feeRange)}
                data-testid="earnings-apply-btn"
              >
                {feesLoading ? 'Loading…' : 'Apply'}
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700"
                disabled={csvLoading}
                onClick={downloadFeesCsv}
                data-testid="earnings-csv-btn"
              >
                <Download className="h-3.5 w-3.5 mr-1" />
                {csvLoading ? 'Exporting…' : 'CSV'}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500">Gross Collected</p>
              <p className="text-xl font-bold text-gray-900" data-testid="earnings-gross">
                K{(feesReport?.totals?.grossCollected ?? 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Your Revenue</p>
              <p className="text-xl font-bold text-green-700" data-testid="earnings-operator-revenue">
                K{(feesReport?.totals?.operatorRevenue ?? 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Platform Fees</p>
              <p className="text-xl font-bold text-indigo-700" data-testid="earnings-platform-fees">
                K{(feesReport?.totals?.platformFees ?? 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Seats Sold</p>
              <p className="text-xl font-bold text-gray-900" data-testid="earnings-seats">
                {feesReport?.totals?.seats ?? 0}
                <span className="text-xs font-normal text-gray-400"> · {feesReport?.totals?.bookings ?? 0} bookings</span>
              </p>
            </div>
          </div>

          {/* Daily breakdown — revenue vs platform fees */}
          {feesReport?.rows?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-indigo-100" data-testid="earnings-daily-chart">
              <p className="text-xs font-medium text-gray-600 mb-2">Daily breakdown</p>
              <div style={{ width: '100%', height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[...feesReport.rows].sort((a, b) => a.key.localeCompare(b.key))}
                    margin={{ top: 4, right: 8, left: -12, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" vertical={false} />
                    <XAxis dataKey="key" tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={(d) => (d || '').slice(5)} />
                    <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #c7d2fe' }}
                      formatter={(value, name) => [`K${Number(value).toLocaleString()}`, name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="operatorRevenue" name="Your Revenue" stackId="a" fill="#15803d" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="platformFees" name="Platform Fees" stackId="a" fill="#4f46e5" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <p className="text-[11px] text-gray-400 mt-3">Realized on boarding / payment · {feeRange.from} → {feeRange.to}</p>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Link to="/bus/operator/fleet">
            <Card className="p-4 hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer h-full">
              <div className="text-center">
                <Bus className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
                <p className="font-medium">Manage Fleet</p>
                <p className="text-xs text-gray-500">Add/Edit buses</p>
              </div>
            </Card>
          </Link>

          <Link to="/bus/operator/routes">
            <Card className="p-4 hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer h-full">
              <div className="text-center">
                <MapPin className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="font-medium">Routes</p>
                <p className="text-xs text-gray-500">Manage routes</p>
              </div>
            </Card>
          </Link>

          <Link to="/bus/operator/schedules">
            <Card className="p-4 hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer h-full">
              <div className="text-center">
                <Calendar className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <p className="font-medium">Schedules</p>
                <p className="text-xs text-gray-500">Trip schedules</p>
              </div>
            </Card>
          </Link>

          <Link to="/bus/operator/bookings">
            <Card className="p-4 hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer h-full">
              <div className="text-center">
                <Ticket className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                <p className="font-medium">Bookings</p>
                <p className="text-xs text-gray-500">View bookings</p>
              </div>
            </Card>
          </Link>

          <Link to="/bus/operator/walk-in">
            <Card className="p-4 hover:border-green-300 hover:bg-green-50 transition-all cursor-pointer h-full border-green-200 bg-green-50">
              <div className="text-center">
                <UserPlus className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="font-medium text-green-700">Walk-in Booking</p>
                <p className="text-xs text-green-600">Station sales</p>
              </div>
            </Card>
          </Link>

          <Link to="/bus/operator/scan" data-testid="operator-scan-link">
            <Card className="p-4 hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer h-full border-indigo-200 bg-indigo-50">
              <div className="text-center">
                <ScanLine className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
                <p className="font-medium text-indigo-700">Scan Ticket</p>
                <p className="text-xs text-indigo-600">Boarding gate</p>
              </div>
            </Card>
          </Link>

          <Link to="/bus/operator/manifest" data-testid="operator-manifest-link">
            <Card className="p-4 hover:border-purple-300 hover:bg-purple-50 transition-all cursor-pointer h-full border-purple-200 bg-purple-50">
              <div className="text-center">
                <ClipboardList className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <p className="font-medium text-purple-700">Boarding Manifest</p>
                <p className="text-xs text-purple-600">Who's on board</p>
              </div>
            </Card>
          </Link>
        </div>

        {/* Today's Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-indigo-600" />
              Today's Overview
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600">Today's Bookings</p>
                <p className="text-2xl font-bold text-blue-700">{dashboard?.todayBookings || 0}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-600">Upcoming Trips</p>
                <p className="text-2xl font-bold text-green-700">{dashboard?.upcomingTrips || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Earnings
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-600">Pending Payout</p>
                <p className="text-2xl font-bold text-green-700">K{dashboard?.pendingPayout?.toLocaleString() || 0}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-600">Rating</p>
                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                  <span className="text-2xl font-bold text-yellow-700">
                    {dashboard?.averageRating?.toFixed(1) || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Recent Schedules */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-600" />
              Recent Schedules
            </h3>
            <Link to="/bus/operator/schedules">
              <Button variant="outline" size="sm">
                View All
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>

          {recentBookings.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No schedules yet</p>
              <Link to="/bus/operator/schedules">
                <Button className="mt-3 bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Schedule
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentBookings.map((schedule, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Bus className="h-5 w-5 text-indigo-600" />
                    <div>
                      <p className="font-medium">
                        {schedule.route?.origin?.city} → {schedule.route?.destination?.city}
                      </p>
                      <p className="text-sm text-gray-500">
                        {schedule.departureTime} • {schedule.daysOfWeek?.join(', ')}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${
                    schedule.status === 'active' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {schedule.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default BusOperatorDashboard;
